import { InsuranceNote } from '@centecomm/portal-service';
import { useInsurance } from '@centecomm/portal-service-react';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { InsuranceActions } from '../actions';
import selectors from '../selectors';

type UseInsuranceNotesStateHook = (insuranceId: number | string) => {
    addNote: (occupantId: number | string, note: string) => Promise<void>;
    areNotesLoaded: boolean;
    areNotesPending: boolean;
    loadNotes: (occupantId: number | string) => Promise<InsuranceNote[]>;
    notesList: InsuranceNote[];
};

const useInsuranceNotesState: UseInsuranceNotesStateHook = (insuranceId) => {
    const dispatch = useDispatch();

    const { getInsuranceNotes, addInsuranceNote } = useInsurance();

    const [pendingInsuranceNotes, setPendingInsuranceNotes] = useState<Record<number, boolean>>([]);

    const areNotesLoaded = useSelector(selectors.areNotesLoaded(insuranceId));

    const areNotesPending =
        Object.hasOwnProperty.call(pendingInsuranceNotes, insuranceId) && pendingInsuranceNotes[insuranceId];

    const loadNotes = async (occupantId: number | string): Promise<InsuranceNote[]> => {
        setPendingInsuranceNotes({
            ...pendingInsuranceNotes,
            [insuranceId]: true,
        });

        const notes = await getInsuranceNotes(+occupantId, +insuranceId);

        dispatch({
            type: InsuranceActions.ADD_INSURANCE_NOTES,
            payload: {
                insuranceId,
                notes,
            },
        });

        setPendingInsuranceNotes({
            ...pendingInsuranceNotes,
            [insuranceId]: false,
        });

        return notes;
    };

    const addNote = async (occupantId: number | string, note: string) => {
        await addInsuranceNote(occupantId, insuranceId, note);

        const notes = await getInsuranceNotes(+occupantId, +insuranceId);

        dispatch({
            type: InsuranceActions.ADD_INSURANCE_NOTES,
            payload: {
                insuranceId,
                notes,
            },
        });
    };

    const notesList = useSelector(selectors.insuranceNotes(insuranceId));

    return {
        addNote,
        areNotesLoaded,
        areNotesPending,
        loadNotes,
        notesList,
    };
};

export default useInsuranceNotesState;
